import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CircleStop,
  FileBarChart,
  History,
  Info,
  Loader2,
  MapPin,
  Settings2,
  Pause,
  Pencil,
  Phone,
  Play,
  RefreshCw,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { useAuthStore } from '@/lib/auth/store';
import {
  useCampaignRetryOptions,
  useCancelCampaign,
  useGenerateCampaignReport,
  usePauseCampaign,
  useResetRetryFailedWebhook,
  useResumeCampaign,
  useRetryCampaign,
  useRetryFailedWebhook,
  useStopCampaign,
} from '../api';
import { EditCampaignCliDialog } from './EditCampaignCliDialog';
import { EditCampaignLocationDialog } from './EditCampaignLocationDialog';
import { STATUS_MAP, type CampaignRow } from '../types';

type ActionKind =
  | 'stop'
  | 'pause'
  | 'resume'
  | 'cancel'
  | 'webhookRetry'
  | 'webhookReset';

interface PendingAction {
  kind: ActionKind;
  campaignId: number;
  campaignName: string;
}

const ACTION_COPY: Record<
  ActionKind,
  {
    title: string;
    confirmLabel: string;
    tone: 'default' | 'destructive';
    body?: string;
  }
> = {
  stop: {
    title: 'Stop this campaign?',
    confirmLabel: 'Stop',
    tone: 'destructive',
  },
  cancel: {
    title: 'Cancel this campaign?',
    confirmLabel: 'Cancel campaign',
    tone: 'destructive',
  },
  pause: {
    title: 'Pause this campaign?',
    confirmLabel: 'Pause',
    tone: 'default',
  },
  resume: {
    title: 'Resume this campaign?',
    confirmLabel: 'Resume',
    tone: 'default',
  },
  webhookRetry: {
    title: 'Retry failed webhooks?',
    confirmLabel: 'Retry',
    tone: 'default',
    body: 'All failed webhook deliveries for this campaign will be re-queued for the next dispatcher tick.',
  },
  webhookReset: {
    title: 'Reset webhook retry counter?',
    confirmLabel: 'Reset',
    tone: 'default',
    body: 'The retry counter for this campaign goes back to zero. Use this when retries have been throttled out by the max-retry cap.',
  },
};

interface CampaignRowActionsProps {
  campaign: CampaignRow;
  /** When true, hide the Edit-campaign menu item (e.g. for read-only views). */
  hideEdit?: boolean;
  /** Override the "View" path. Defaults to /voice-campaign-info/:id. */
  viewPathPrefix?: string;
}

export function CampaignRowActions({
  campaign,
  hideEdit = false,
  viewPathPrefix = '/voice-campaign-info',
}: CampaignRowActionsProps) {
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [editCliOpen, setEditCliOpen] = useState(false);
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  /** Side drawer (Settings2 button → opens the console). */
  const [sheetOpen, setSheetOpen] = useState(false);

  /** Close the side drawer whenever we open a child confirm/dialog so the
   *  user isn't looking at two stacked overlays. */
  const openInDrawer = (cb: () => void) => () => {
    setSheetOpen(false);
    cb();
  };

  /** Pre-fill location IDs from the campaign row's `locationList` if available. */
  const currentLocationIds = useMemo(() => {
    const raw = campaign.locationList;
    if (!raw) return [];
    // Legacy serializes locations as either a comma-joined string of IDs or
    // a comma-joined string of names. We hand it back as-is and let the
    // dialog re-derive from the user's location library.
    return String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [campaign.locationList]);

  const stop = useStopCampaign();
  const pause = usePauseCampaign();
  const resume = useResumeCampaign();
  const cancel = useCancelCampaign();
  const retryWebhook = useRetryFailedWebhook();
  const resetRetry = useResetRetryFailedWebhook();
  const generate = useGenerateCampaignReport();

  const status = campaign.campaignStatus;
  const RUNNING = STATUS_MAP.Running as number;
  const COMPLETE = STATUS_MAP.Complete as number;
  const PAUSED = STATUS_MAP.Paused as number;
  const READY = STATUS_MAP.Ready as number;

  const isViewable =
    campaign.templateId === 0 ||
    campaign.templateId === 1 ||
    campaign.templateId === 2;

  const mutationFor = (kind: ActionKind) => {
    switch (kind) {
      case 'stop':
        return stop;
      case 'pause':
        return pause;
      case 'resume':
        return resume;
      case 'cancel':
        return cancel;
      case 'webhookRetry':
        return retryWebhook;
      case 'webhookReset':
        return resetRetry;
    }
  };

  const successCopy = (kind: ActionKind) =>
    ({
      stop: 'Stop request submitted',
      pause: 'Campaign paused',
      resume: 'Campaign resumed',
      cancel: 'Campaign cancelled',
      webhookRetry: 'Failed webhooks re-queued',
      webhookReset: 'Webhook retry counter reset',
    })[kind];

  const handleConfirm = () => {
    if (!pending) return;
    const mut = mutationFor(pending.kind);
    mut.mutate(
      { campaignId: pending.campaignId },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? successCopy(pending.kind));
          setPending(null);
        },
        onError: () => {
          toast.error(`Could not ${pending.kind} the campaign.`);
          setPending(null);
        },
      },
    );
  };

  const isAnyPending =
    stop.isPending ||
    pause.isPending ||
    resume.isPending ||
    cancel.isPending ||
    retryWebhook.isPending ||
    resetRetry.isPending;

  const generateFullReport = () => {
    generate.mutate(
      { campaignId: campaign.campaignId, reportType: 'full' },
      {
        onSuccess: (res) => toast.success(res?.message ?? 'Report queued'),
        onError: () => toast.error('Could not generate the report.'),
      },
    );
  };


  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={`Open settings for ${campaign.campaignName}`}
        title="Settings"
        disabled={isAnyPending}
        onClick={() => setSheetOpen(true)}
      >
        <Settings2 className="h-4 w-4" />
      </Button>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <CampaignStatusBadge status={campaign.campaignStatus} />
            </div>
            <SheetTitle className="truncate">{campaign.campaignName}</SheetTitle>
            <SheetDescription>
              Campaign #{campaign.campaignId} — manage lifecycle, reports and
              edit allocations.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* ---- View ---- */}
            {isViewable && (
              <ActionSection title="View">
                <Button
                  asChild
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setSheetOpen(false)}
                >
                  <Link to={`${viewPathPrefix}/${campaign.campaignId}`}>
                    <Info className="h-4 w-4" />
                    View info
                  </Link>
                </Button>
              </ActionSection>
            )}

            {/* ---- Edit ---- */}
            {!hideEdit &&
              (status === READY || status === RUNNING || status === PAUSED) && (
                <ActionSection title="Edit">
                  {!isSubOrSuper && status === READY && (
                    <Button
                      asChild
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setSheetOpen(false)}
                    >
                      <Link to={`/update-voice-campaign/${campaign.campaignId}`}>
                        <Pencil className="h-4 w-4" />
                        Edit campaign
                      </Link>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={openInDrawer(() => setEditCliOpen(true))}
                  >
                    <Phone className="h-4 w-4" />
                    Edit CLIs
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={openInDrawer(() => setEditLocationOpen(true))}
                  >
                    <MapPin className="h-4 w-4" />
                    Edit locations
                  </Button>
                </ActionSection>
              )}

            {/* ---- Lifecycle ---- */}
            {(status === RUNNING || status === PAUSED) && (
              <ActionSection title="Lifecycle">
                {status === RUNNING && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={openInDrawer(() =>
                      setPending({
                        kind: 'pause',
                        campaignId: campaign.campaignId,
                        campaignName: campaign.campaignName,
                      }),
                    )}
                  >
                    <Pause className="h-4 w-4" />
                    Pause
                  </Button>
                )}
                {status === PAUSED && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={openInDrawer(() =>
                      setPending({
                        kind: 'resume',
                        campaignId: campaign.campaignId,
                        campaignName: campaign.campaignName,
                      }),
                    )}
                  >
                    <Play className="h-4 w-4" />
                    Resume
                  </Button>
                )}
                {status === RUNNING && (
                  <>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-destructive hover:text-destructive"
                      onClick={openInDrawer(() =>
                        setPending({
                          kind: 'stop',
                          campaignId: campaign.campaignId,
                          campaignName: campaign.campaignName,
                        }),
                      )}
                    >
                      <CircleStop className="h-4 w-4" />
                      Stop
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-destructive hover:text-destructive"
                      onClick={openInDrawer(() =>
                        setPending({
                          kind: 'cancel',
                          campaignId: campaign.campaignId,
                          campaignName: campaign.campaignName,
                        }),
                      )}
                    >
                      <XCircle className="h-4 w-4" />
                      Cancel
                    </Button>
                  </>
                )}
              </ActionSection>
            )}

            {/* ---- Reports ---- */}
            {(status === COMPLETE || campaign.fullReportStatus === '2') && (
              <ActionSection title="Reports">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={openInDrawer(generateFullReport)}
                  disabled={generate.isPending}
                >
                  <FileBarChart className="h-4 w-4" />
                  Generate full report
                </Button>
                {status === COMPLETE &&
                  String(campaign.webhookLog) === '1' && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={openInDrawer(() =>
                        generate.mutate(
                          {
                            campaignId: campaign.campaignId,
                            reportType: 'webhook',
                          },
                          {
                            onSuccess: (res) =>
                              toast.success(res?.message ?? 'Report queued'),
                            onError: () =>
                              toast.error('Could not generate the report.'),
                          },
                        ),
                      )}
                      disabled={generate.isPending}
                    >
                      <FileBarChart className="h-4 w-4" />
                      Generate webhook report
                    </Button>
                  )}
                {status === COMPLETE && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={openInDrawer(() => setRetryOpen(true))}
                  >
                    <History className="h-4 w-4" />
                    Retry campaign
                  </Button>
                )}
              </ActionSection>
            )}

            {/* ---- Webhooks ---- */}
            {status === COMPLETE &&
              (Number(campaign.webhookFailedCount ?? 0) > 0 ||
                String(campaign.webhookLog) === '1') && (
                <ActionSection title="Webhooks">
                  {Number(campaign.webhookFailedCount ?? 0) > 0 && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={openInDrawer(() =>
                        setPending({
                          kind: 'webhookRetry',
                          campaignId: campaign.campaignId,
                          campaignName: campaign.campaignName,
                        }),
                      )}
                    >
                      <RefreshCw className="h-4 w-4" />
                      Retry failed webhooks
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={openInDrawer(() =>
                      setPending({
                        kind: 'webhookReset',
                        campaignId: campaign.campaignId,
                        campaignName: campaign.campaignName,
                      }),
                    )}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset retry counter
                  </Button>
                </ActionSection>
              )}
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending ? ACTION_COPY[pending.kind].title : ''}
        description={
          pending && (
            <>
              <span className="font-medium text-foreground">
                {pending.campaignName}
              </span>{' '}
              — {ACTION_COPY[pending.kind].body ??
                `will be ${pending.kind === 'cancel' ? 'cancelled' : `${pending.kind}d`}.`}
            </>
          )
        }
        confirmLabel={pending ? ACTION_COPY[pending.kind].confirmLabel : 'Confirm'}
        tone={pending ? ACTION_COPY[pending.kind].tone : 'default'}
        loading={isAnyPending}
        onConfirm={handleConfirm}
      />

      <EditCampaignCliDialog
        open={editCliOpen}
        onOpenChange={setEditCliOpen}
        campaignId={campaign.campaignId}
        campaignName={campaign.campaignName}
      />

      <EditCampaignLocationDialog
        open={editLocationOpen}
        onOpenChange={setEditLocationOpen}
        campaignId={campaign.campaignId}
        campaignName={campaign.campaignName}
        currentLocationIds={currentLocationIds}
      />

      <RetryCampaignDialog
        open={retryOpen}
        onOpenChange={setRetryOpen}
        campaignId={campaign.campaignId}
        campaignName={campaign.campaignName}
      />
    </>
  );
}

/* ============================================================
 *  Drawer section wrapper — small heading + a vertical stack of action
 *  buttons. Renders nothing if all children are falsy / hidden.
 * ============================================================ */

function ActionSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h4 className="mb-2 text-2xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

/* ============================================================
 *  Retry-campaign dialog
 * ============================================================ */

function RetryCampaignDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: number;
  campaignName: string;
}) {
  const userId = useAuthStore((s) => s.userId);
  const options = useCampaignRetryOptions(open ? campaignId : null);
  const retry = useRetryCampaign();

  /**
   * Map of optionName → checked. Driven by the backend response. Re-resets
   * when the dialog closes so subsequent opens start fresh.
   */
  const [picks, setPicks] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) setPicks({});
  }, [open]);

  const allKeys = useMemo(
    () => (options.data ?? []).map((o) => o.optionName),
    [options.data],
  );
  const allSelected =
    allKeys.length > 0 && allKeys.every((k) => picks[k]);
  const anySelected = allKeys.some((k) => picks[k]);

  const toggle = (k: string) =>
    setPicks((prev) => ({ ...prev, [k]: !prev[k] }));

  const toggleAll = () => {
    if (allSelected) setPicks({});
    else setPicks(Object.fromEntries(allKeys.map((k) => [k, true])));
  };

  const handleConfirm = () => {
    if (!userId) {
      toast.error('Cannot determine current user.');
      return;
    }
    if (!anySelected) {
      toast.error('Pick at least one option to retry.');
      return;
    }
    // Legacy semantic: when every option is picked, send the literal "All";
    // otherwise send the colon-joined list of `optionName` values.
    const selectedOptions = allSelected
      ? 'All'
      : allKeys.filter((k) => picks[k]).join(':');

    retry.mutate(
      { campaignId, userId: Number(userId), selectedOptions },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Retry scheduled');
          onOpenChange(false);
        },
        onError: () => toast.error('Could not schedule the retry.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Retry campaign</DialogTitle>
          <DialogDescription>
            Pick which call outcomes to retry for{' '}
            <span className="font-medium text-foreground">{campaignName}</span>.
          </DialogDescription>
        </DialogHeader>
        {options.isLoading ? (
          <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading retry options…
          </div>
        ) : (options.data?.length ?? 0) === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card/50 py-6 text-center text-sm text-muted-foreground">
            No retryable outcomes for this campaign.
          </div>
        ) : (
          <div className="grid gap-2">
            <label className="flex cursor-pointer items-center gap-2 rounded border border-border bg-card px-3 py-2 text-sm font-medium">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
              />
              <span>Select all</span>
            </label>
            <div className="grid gap-1.5">
              {(options.data ?? []).map((opt) => (
                <label
                  key={opt.optionName}
                  className="flex cursor-pointer items-center justify-between gap-2 rounded border border-border bg-card px-3 py-2 text-sm hover:border-ring"
                >
                  <span className="flex items-center gap-2">
                    <Checkbox
                      checked={Boolean(picks[opt.optionName])}
                      onCheckedChange={() => toggle(opt.optionName)}
                    />
                    {opt.optionName}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {opt.count}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={retry.isPending || !anySelected}
          >
            {retry.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Schedule retry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
